/**
 * Box categories: what the storefront's boxes page filters by. One fixed list, boxed's
 * `SiteCategory` (`services/core/account/Account.ts` there) with boxed-frontend's labels
 * (`utils/categoryDisplay.ts`): each code is a game (Pokemon, Baseball, Yu-Gi-Oh!) or a family of
 * games (Sports, Other), and a family's games name it as their parent, as boxed's
 * `CATEGORY_METADATA` does.
 *
 * Nothing assigns a box to a category. A catalog item names its game with a tag (the boxed
 * import copies the AssetGroup's tags), and a box is in every game its current version's items
 * name and in each of those games' families (`boxCategoriesOfGames`). Which categories a
 * storefront offers is the environment's own choice (`storefront_configs.box_categories`, every
 * one until an editor saves a choice), and the forge builds only with items in those.
 */
import { z } from "zod";

/** Every box category, in the order a storefront lists them: each family, then its games. */
export const BOX_CATEGORIES = [
  "POKEMON",
  "MTG",
  "ONE_PIECE",
  "RIFTBOUND",
  "SPORTS",
  "BASEBALL",
  "BASKETBALL",
  "FOOTBALL",
  "HOCKEY",
  "SOCCER",
  "RACING",
  "COMBAT_SPORTS",
  "GOLF",
  "OTHER",
  "LORCANA",
  "DRAGON_BALL",
  "STAR_WARS",
  "YUGIOH",
  "DIGIMON",
  "GUNDAM",
  "COUNTERSTRIKE",
  "RUST",
  "NFTS",
  "REDDIT_AVATARS",
  "PARALLEL",
] as const;
export type BoxCategory = (typeof BOX_CATEGORIES)[number];

export const boxCategorySchema = z.enum(BOX_CATEGORIES);

/**
 * A category's label, the family it belongs to (null on a family or a game with none), and
 * whether it is one of boxed's digital lines (skins, NFTs) rather than physical cards.
 */
export const BOX_CATEGORY_INFO: Record<
  BoxCategory,
  { label: string; parent: BoxCategory | null; digital: boolean }
> = {
  POKEMON: { label: "Pokemon", parent: null, digital: false },
  MTG: { label: "MTG", parent: null, digital: false },
  ONE_PIECE: { label: "One Piece", parent: null, digital: false },
  RIFTBOUND: { label: "Riftbound", parent: null, digital: false },
  SPORTS: { label: "Sports", parent: null, digital: false },
  BASEBALL: { label: "Baseball", parent: "SPORTS", digital: false },
  BASKETBALL: { label: "Basketball", parent: "SPORTS", digital: false },
  FOOTBALL: { label: "Football", parent: "SPORTS", digital: false },
  HOCKEY: { label: "Hockey", parent: "SPORTS", digital: false },
  SOCCER: { label: "Soccer", parent: "SPORTS", digital: false },
  RACING: { label: "Racing", parent: "SPORTS", digital: false },
  COMBAT_SPORTS: { label: "Combat", parent: "SPORTS", digital: false },
  GOLF: { label: "Golf", parent: "SPORTS", digital: false },
  OTHER: { label: "Other", parent: null, digital: false },
  LORCANA: { label: "Lorcana", parent: "OTHER", digital: false },
  DRAGON_BALL: { label: "Dragon Ball", parent: "OTHER", digital: false },
  STAR_WARS: { label: "Star Wars", parent: "OTHER", digital: false },
  YUGIOH: { label: "Yu-Gi-Oh!", parent: "OTHER", digital: false },
  DIGIMON: { label: "Digimon", parent: "OTHER", digital: false },
  GUNDAM: { label: "Gundam", parent: "OTHER", digital: false },
  COUNTERSTRIKE: { label: "Counter-Strike 2", parent: null, digital: true },
  RUST: { label: "Rust", parent: null, digital: true },
  NFTS: { label: "Digital Collectibles", parent: null, digital: true },
  REDDIT_AVATARS: { label: "Reddit Avatars", parent: null, digital: true },
  PARALLEL: { label: "Parallel", parent: null, digital: true },
};

const POSITION = new Map<string, number>(BOX_CATEGORIES.map((code, index) => [code, index]));

/** Whether a string is one of the list's codes. */
export function isBoxCategory(value: unknown): value is BoxCategory {
  return typeof value === "string" && POSITION.has(value);
}

/** Codes in list order, each once. */
export function sortBoxCategories(codes: Iterable<BoxCategory>): BoxCategory[] {
  return [...new Set(codes)].sort((a, b) => (POSITION.get(a) ?? 0) - (POSITION.get(b) ?? 0));
}

/**
 * Codes as a saved choice holds them: in list order, each once, and no game whose family is off,
 * since turning a family off turns its games off with it.
 */
export function normalizeBoxCategories(codes: Iterable<BoxCategory>): BoxCategory[] {
  const sorted = sortBoxCategories(codes);
  const on = new Set(sorted);
  return sorted.filter((code) => {
    const parent = BOX_CATEGORY_INFO[code].parent;
    return parent === null || on.has(parent);
  });
}

/** A family's games, in list order; `[]` for a game. */
export function boxCategoryChildren(family: BoxCategory): BoxCategory[] {
  return BOX_CATEGORIES.filter((code) => BOX_CATEGORY_INFO[code].parent === family);
}

/**
 * The game an item's tags name: the most specific category among them, so `["SPORTS",
 * "BASEBALL"]` is Baseball (boxed's `getLeafCategoryFromTags`). Tags outside the list are ignored;
 * null when none is in it.
 */
export function gameOfTags(tags: readonly string[] | null | undefined): BoxCategory | null {
  const named = (tags ?? []).filter(isBoxCategory);
  return (
    named.find((code) => !named.some((other) => BOX_CATEGORY_INFO[other].parent === code)) ?? null
  );
}

/** The family of the game an item's tags name, or the game itself when it has none. */
export function gameFamilyOfTags(tags: readonly string[] | null | undefined): BoxCategory | null {
  const game = gameOfTags(tags);
  return game === null ? null : (BOX_CATEGORY_INFO[game].parent ?? game);
}

/** The categories a box whose items name these games is in: each game and its family. */
export function boxCategoriesOfGames(games: Iterable<BoxCategory>): BoxCategory[] {
  const codes: BoxCategory[] = [];
  for (const game of games) {
    codes.push(game);
    const parent = BOX_CATEGORY_INFO[game].parent;
    if (parent) codes.push(parent);
  }
  return sortBoxCategories(codes);
}

/** A category as a URL names it: `ONE_PIECE` is `one-piece`. */
export function boxCategorySlug(code: BoxCategory): string {
  return code.toLowerCase().replaceAll("_", "-");
}

/** The category a URL slug names, or null for one the list does not have. */
export function boxCategoryFromSlug(slug: unknown): BoxCategory | null {
  if (typeof slug !== "string") return null;
  const code = slug.toUpperCase().replaceAll("-", "_");
  return isBoxCategory(code) ? code : null;
}
